// Globals
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Theme
import { COLORS } from '@/theme';

// Models
import { FiscalReceiptStatus, IFiscalReceipt } from '@/models/orders.model';

// Styled
import {
  StyledEmpty,
  StyledError,
  StyledLabel,
  StyledLink,
  StyledReceipt,
  StyledRow,
  StyledStatusBadge,
  StyledValue,
} from './Styled';

type TFiscalReceiptsBlockProps = {
  receipts?: IFiscalReceipt[];
};

const getStatusColor = (status: FiscalReceiptStatus | null): string => {
  switch (status) {
    case 'done':
      return COLORS.GREEN;
    case 'failed':
      return COLORS.RED;
    case 'pending':
      return COLORS.ORANGE;
    default:
      return COLORS.NORMAL_GRAY;
  }
};

const FiscalReceiptsBlock = ({ receipts }: TFiscalReceiptsBlockProps) => {
  const { t } = useTranslation(['orders']);

  // Map receipt id -> fiscal code, to show which sale a return refers to.
  const fiscalCodeById = useMemo(() => {
    const map: Record<string, string> = {};
    (receipts ?? []).forEach((r) => {
      if (r.checkbox_receipt_id && r.fiscal_code) {
        map[r.checkbox_receipt_id] = r.fiscal_code;
      }
    });
    return map;
  }, [receipts]);

  if (!receipts || receipts.length === 0) {
    return <StyledEmpty>{t('fiscal_receipts_empty')}</StyledEmpty>;
  }

  return (
    <>
      {receipts.map((receipt) => {
        const isReturn = receipt.type === 'return';
        const relatedCode = receipt.related_checkbox_receipt_id
          ? fiscalCodeById[receipt.related_checkbox_receipt_id]
          : null;

        return (
          <StyledReceipt key={receipt.id}>
            <StyledRow>
              <StyledValue>
                {receipt.legal_type ? t(`orders_${receipt.legal_type}`) : '—'}
                {isReturn && (
                  <StyledStatusBadge $bg={COLORS.BLUE_PRIMARY} style={{ marginLeft: 8 }}>
                    {t('fiscal_type_return')}
                  </StyledStatusBadge>
                )}
              </StyledValue>
              <StyledStatusBadge $bg={getStatusColor(receipt.status)}>
                {t(`fiscal_status_${receipt.status ?? 'not_applicable'}`)}
              </StyledStatusBadge>
            </StyledRow>

            {isReturn && (
              <StyledRow>
                <StyledLabel>{t('fiscal_return_of')}</StyledLabel>
                <StyledValue>{relatedCode ?? '—'}</StyledValue>
              </StyledRow>
            )}

            {receipt.cash_register?.name && (
              <StyledRow>
                <StyledLabel>{t('fiscal_cash_register')}</StyledLabel>
                <StyledValue>{receipt.cash_register.name}</StyledValue>
              </StyledRow>
            )}

            {receipt.fiscal_code && (
              <StyledRow>
                <StyledLabel>{t('fiscal_code')}</StyledLabel>
                <StyledValue>{receipt.fiscal_code}</StyledValue>
              </StyledRow>
            )}

            {receipt.receipt_url && (
              <StyledLink href={receipt.receipt_url} target="_blank" rel="noopener noreferrer">
                {t('fiscal_open_receipt')}
              </StyledLink>
            )}

            {receipt.error && <StyledError>{receipt.error}</StyledError>}
          </StyledReceipt>
        );
      })}
    </>
  );
};

export default FiscalReceiptsBlock;
